import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { get } from 'lodash';
import { Info } from 'lucide-react';

import { OrderSchemaType } from '../order.schema';
import { Client, Seller, Supplier } from '../types';
import { FormGrid, FormGroup, Label, Select, Separator, InfoLink } from '../style';
import InfoTooltip from '../../../../../components/InfoTooltip';

interface MainInfosSectionProps {
  suppliers: Supplier[];
  clients: Client[];
  sellers: Seller[];
}

const MainInfosSection: React.FC<MainInfosSectionProps> = ({ suppliers, clients, sellers }) => {
  const { control, watch, formState: { errors }, setValue } = useFormContext<OrderSchemaType>();

  const [activeInfo, setActiveInfo] = useState<string | null>(null);

  const handleInfoToggle = (type: string, hasDetails: boolean) => {
    if (!hasDetails) return;
    setActiveInfo(activeInfo === type ? null : type);
  };

  const selectedSupplierId = watch('supplierId');
  const selectedClientId = watch('clientId');
  const selectedSellerId = watch('sellerId');

  const selectedSupplier = suppliers.find(s => s.id === Number(selectedSupplierId));
  const selectedClient = clients.find(c => c.id === Number(selectedClientId));
  const selectedSeller = sellers.find(s => s.id === Number(selectedSellerId));

  const handleSupplierChange = (supplierName: string) => {
    const supplier = suppliers.find(s => s.name === supplierName);
    setValue('supplier', supplierName);
    setValue('supplierId', supplier ? Number(supplier.id) : undefined);
  };

  const handleClientChange = (clientName: string) => {
    const client = clients.find(c => c.name === clientName);
    setValue('client', clientName);
    setValue('clientId', client ? Number(client.id) : undefined);
  };

  const handleSellerChange = (sellerName: string) => {
    const seller = sellers.find(s => s.name === sellerName);
    setValue('seller', sellerName);
    setValue('sellerId', seller ? Number(seller.id) : undefined);
  };

  return (
    <>
      <FormGrid>
        <FormGroup style={{ position: 'relative' }}>
          <Label htmlFor="supplier">Fornecedor</Label>
          <Controller
            name="supplierId"
            control={control}
            render={({ field }) => (
              <Select
                id="supplier"
                {...field}
                value={field.value || ''}
                hasError={!!get(errors, 'supplierId')}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  const supplier = suppliers.find(s => Number(s.id) === value);
                  const supplierId = !isNaN(value) ? value : undefined;
                  field.onChange(supplierId);
                  setValue('supplier', supplier ? supplier.name : '');
                  setValue('supplierId', supplierId);
                  setActiveInfo(null);
                }}
              >
                <option value="">Selecione um fornecedor</option>
                {suppliers.map((s, index) => <option key={`supplier-${s.id || index}`} value={s.id}>{s.name}</option>)}
              </Select>
            )}
          />
          <InfoLink
            disabled={!selectedSupplier}
            onClick={() => handleInfoToggle('supplier', !!selectedSupplier)}
          >
            <Info size={16} />
            <span>Mais informações</span>
          </InfoLink>
          {activeInfo === 'supplier' && selectedSupplier && (
            <InfoTooltip details={Object.fromEntries(Object.entries(selectedSupplier).map(([k, v]) => [k, String(v ?? '')]))} />
          )}
        </FormGroup>

        <FormGroup style={{ position: 'relative' }}>
          <Label htmlFor="seller">Vendedor</Label>
          <Controller
            name="sellerId"
            control={control}
            render={({ field }) => (
              <Select
                id="seller"
                {...field}
                value={field.value || ''}
                hasError={!!get(errors, 'sellerId')}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  const seller = sellers.find(s => Number(s.id) === value);
                  const sellerId = !isNaN(value) ? value : undefined;
                  field.onChange(sellerId);
                  setValue('seller', seller ? seller.name : '');
                  setValue('sellerId', sellerId);
                  setActiveInfo(null);
                }}
              >
                <option value="">Selecione um vendedor</option>
                {sellers.map((s, index) => <option key={`seller-${s.id || index}`} value={s.id}>{s.name}</option>)}
              </Select>
            )}
          />
          <InfoLink
            disabled={!selectedSeller}
            onClick={() => handleInfoToggle('seller', !!selectedSeller)}
          >
            <Info size={16} />
            <span>Mais informações</span>
          </InfoLink>
          {activeInfo === 'seller' && selectedSeller && (
            <InfoTooltip details={Object.fromEntries(Object.entries(selectedSeller).map(([k, v]) => [k, String(v ?? '')]))} />
          )}
        </FormGroup>

        <FormGroup style={{ position: 'relative' }}>
          <Label htmlFor="client">Cliente</Label>
          <Controller
            name="clientId"
            control={control}
            render={({ field }) => (
              <Select
                id="client"
                {...field}
                value={field.value || ''}
                hasError={!!get(errors, 'clientId')}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  const client = clients.find(c => Number(c.id) === value);
                  const clientId = !isNaN(value) ? value : undefined;
                  field.onChange(clientId);
                  setValue('client', client ? client.name : '');
                  setValue('clientId', clientId);
                  setActiveInfo(null);
                }}
              >
                <option value="">Selecione um cliente</option>
                {clients.map((c, index) => <option key={`client-${c.id || index}`} value={c.id}>{c.name}</option>)}
              </Select>
            )}
          />
          <InfoLink
            disabled={!selectedClient}
            onClick={() => handleInfoToggle('client', !!selectedClient)}
          >
            <Info size={16} />
            <span>Mais informações</span>
          </InfoLink>
          {activeInfo === 'client' && selectedClient && (
            <InfoTooltip details={Object.fromEntries(Object.entries(selectedClient).map(([k, v]) => [k, String(v ?? '')]))} />
          )}
        </FormGroup>
      </FormGrid>
      <Separator />
    </>
  );
};

export default MainInfosSection;